import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-impersonation-banner>` says about itself.
 *
 * A safety surface, not a notice. While an administrator is signed in as somebody else, every
 * action taken is attributed to that other person, and the expensive failure is forgetting — going
 * back to work with the session still switched and changing records as a colleague. So the strip is
 * loud (the brand accent across the full width), it is at the very top of the shell above
 * everything else, and it carries the way OUT of the state it is announcing on every page.
 *
 * Three consequences of that, all visible in the source:
 *
 *  - It is not dismissable. The only control on it ends the impersonation.
 *  - It is presentational and event-only, holding no session logic of its own: pressing the exit
 *    raises `impersonation-end` and the host ends the override. It also does not hide itself when
 *    pressed — the host takes the strip away by clearing `target-label`, which means the strip does
 *    not disappear until the switch has actually been undone.
 *  - With no `target-label` it sets `hidden` on itself and renders nothing, so a shell can leave it
 *    permanently in the frame and drive it entirely by that one attribute.
 *
 * For anyone restyling: the lettering, the hairline under the bar and the exit control's border and
 * focus ring all take the on-accent ink, `--caos-color-accent-contrast` (CAOS-652), so a brand whose
 * accent is pale sets a darker ink there and the whole strip follows. The exit control's fill is a
 * black shade over the bar rather than a colour of its own, so it darkens whatever accent it sits on.
 */
const docs: ComponentDocs = {
  aliases: ['login as bar', 'view as banner', 'acting as strip', 'switch user banner', 'sudo banner', 'masquerade bar'],

  answers: [
    'show that an administrator is viewing the app as somebody else',
    'give a way back to your own session from any page',
    'make an impersonated session impossible to forget',
    'warn that anything done now will be attributed to another user',
  ],

  // A frame part. It spans the whole shell above the top bar and appears exactly once, so it belongs
  // on a seam and nowhere else — it is not something a page or a record layout places.
  placements: ['seam'],

  inputs: [
    {
      name: 'targetLabel',
      attribute: 'target-label',
      type: 'text',
      description:
        'The user being viewed as, drawn in bold in the middle of the strip. Its PRESENCE is what makes ' +
        'the strip visible: with it unset or empty the element hides itself and renders nothing.',
      required: true,
    },
    {
      name: 'realLabel',
      attribute: 'real-label',
      type: 'text',
      description:
        'The administrator\'s own name, drawn as a lighter "signed in as …" tail. Unset, the tail is ' +
        'not drawn. Worth setting on a shared machine, where "who am I really" is the question the ' +
        'strip is there to answer.',
    },
    {
      name: 'product',
      attribute: 'product',
      type: 'text',
      description: 'The platform name in the copy — "Viewing <product> as …".',
      default: 'the app',
    },
    {
      name: 'expiresAt',
      attribute: 'expires-at',
      type: 'text',
      description:
        'When the borrowed session runs out, as an ISO timestamp. Set it and the strip carries a ' +
        'countdown beside the exit, coarse (minutes, rounded up) until the last minute and then ' +
        'seconds. Unset, unparseable, or already past, and no countdown is drawn at all — a wrong ' +
        'number here is worse than none, because somebody plans around it.',
    },
  ],

  events: [
    {
      name: 'impersonation-end',
      when:
        'The "End Login As" control is pressed. It bubbles and crosses the shadow boundary so the shell ' +
        'can listen at its root. The strip does NOT hide itself or undo anything — the host ends the ' +
        'override and then clears `target-label`, so the strip stays up until the session is genuinely ' +
        'back to the real user.',
      carries: 'nothing',
    },
  ],

  // Nothing is slotted: the whole strip is generated from the three attributes.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-accent', controls: 'The fill of the whole strip — the one thing about it a tenant\'s brand moves.' },
    { token: '--caos-font-mono', controls: 'The typeface of the message and the exit control.' },
    { token: '--caos-radius-sm', controls: 'The corners of the exit control.' },
    {
      token: '--caos-color-accent-contrast',
      controls:
        'The lettering, the hairline under the bar, and the exit control\'s border and focus ring. White ' +
        'by default; a brand with a pale accent sets a darker ink here.',
    },
  ],

  parts: [
    { part: 'banner', is: 'the banner strip' },
    { part: 'remaining', is: 'the time left before the borrowed session runs out' },
    { part: 'exit', is: 'the control that ends the borrowed session' },
  ],

  methods: [],

  placing:
    'At the very top of the shell, above the top bar and full width, outside any scrolling region — ' +
    'the point of it is that it is present on every page and never scrolls away. It sets its own ' +
    'stacking so the frosted bar beneath cannot cover it. Below 720px it tightens up and the "signed ' +
    'in as" tail drops onto its own line rather than being cut off. A shell can leave it mounted ' +
    'permanently and drive it with `target-label` alone.',

  whenToUse:
    'Whenever a session is running as somebody other than the person signed in — support impersonation, ' +
    'an administrator reproducing a report, a role check. It is the strip that stops that state from ' +
    'being invisible.',

  insteadUse: [
    {
      name: 'NoticeBanner',
      when:
        'The message is about the PAGE rather than the session — a condition of this surface, a ' +
        'decision to make here. A notice banner belongs inside the page and scrolls with it; this ' +
        'strip belongs to the frame and must not.',
    },
    {
      name: 'Topbar',
      when:
        'What is wanted is the control that STARTS the switch, and the account menu that offers it. ' +
        'That is the top bar\'s job; this strip is what the switch leaves behind.',
    },
    {
      name: 'StatusPill',
      when:
        'The environment or mode needs marking somewhere quiet rather than announcing across the top ' +
        'of the screen — the `dev` pill is for that.',
    },
  ],

  accessibility:
    'The strip is a polite live region (`role="status"`, `aria-live="polite"`), so it is announced when ' +
    'it appears without cutting across whatever is being read, and it is genuinely removed from the ' +
    'accessibility tree when there is no target rather than being drawn empty. The exit is a real ' +
    'button with a visible text label — not an icon alone — and carries its own light focus ring, ' +
    'because the ordinary accent ring would be invisible against the accent fill. Left to the author: ' +
    'binding `impersonation-end` (an exit control that does nothing is worse here than no strip at ' +
    'all), and keeping the strip outside any container that scrolls or clips.',

  examples: [
    {
      key: 'active',
      label: 'A session running as somebody else',
      summary:
        'The strip as the shell draws it: who is being viewed as, who is really signed in, and the exit ' +
        'that is reachable from every page.',
      placement: {
        component: 'caos-impersonation-banner',
        inputs: {
          targetLabel: 'Marcus Reed',
          realLabel: 'Dana Whitfield',
          product: 'Cloud Atlantis OS',
        },
      },
    },
    {
      key: 'minimal',
      label: 'The least it can be told',
      summary:
        'Only the user being viewed as: the copy falls back to "the app" and the "signed in as" tail is ' +
        'not drawn — which is the version to avoid on a shared machine, where the missing half is the ' +
        'half that says who you really are.',
      placement: {
        component: 'caos-impersonation-banner',
        inputs: { targetLabel: 'Marcus Reed' },
      },
    },
  ],
};

export default docs;
